import { Progress } from '@/components/ui/progress';
import type { BudgetRange, AuthorityLevel, NeedUrgency, DealTimeline } from '@/types/models';

interface BantBreakdownProps {
  budget: { confirmed: boolean; range: BudgetRange };
  authority: { isDecisionMaker: boolean; level: AuthorityLevel };
  need: { identified: boolean; urgency: NeedUrgency };
  timeline: DealTimeline;
  totalScore: number;
}

const BUDGET_SCORES: Record<BudgetRange, number> = {
  unknown: 0, under_10k: 10, '10k_50k': 15, '50k_100k': 20, over_100k: 25,
};

const AUTHORITY_SCORES: Record<AuthorityLevel, number> = {
  influencer: 5, evaluator: 10, decision_maker: 20, champion: 25,
};

const NEED_SCORES: Record<NeedUrgency, number> = {
  none: 0, low: 5, medium: 10, high: 20, critical: 25,
};

const TIMELINE_SCORES: Record<DealTimeline, number> = {
  no_timeline: 0, over_12mo: 5, '6_12mo': 10, '3_6mo': 20, under_3mo: 25,
};

const LABELS: Record<string, string> = {
  unknown: 'Unknown', under_10k: '<$10K', '10k_50k': '$10K–$50K', '50k_100k': '$50K–$100K', over_100k: '$100K+',
  influencer: 'Influencer', evaluator: 'Evaluator', decision_maker: 'Decision Maker', champion: 'Champion',
  none: 'None', low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical',
  no_timeline: 'No Timeline', over_12mo: '12+ Months', '6_12mo': '6–12 Months', '3_6mo': '3–6 Months', under_3mo: '<3 Months',
};

export default function BantBreakdown({ budget, authority, need, timeline, totalScore }: BantBreakdownProps) {
  const budgetScore = (budget.confirmed ? 5 : 0) + (BUDGET_SCORES[budget.range] ?? 0);
  const authorityScore = (authority.isDecisionMaker ? 5 : 0) + (AUTHORITY_SCORES[authority.level] ?? 0);
  const needScore = (need.identified ? 5 : 0) + (NEED_SCORES[need.urgency] ?? 0);
  const timelineScore = TIMELINE_SCORES[timeline] ?? 0;

  const dimensions = [
    { label: 'Budget', score: Math.min(budgetScore, 25), detail: `${budget.confirmed ? '✓ Confirmed' : '✗ Unconfirmed'} · ${LABELS[budget.range]}` },
    { label: 'Authority', score: Math.min(authorityScore, 25), detail: `${authority.isDecisionMaker ? '✓ Decision Maker' : '✗ Not DM'} · ${LABELS[authority.level]}` },
    { label: 'Need', score: Math.min(needScore, 25), detail: `${need.identified ? '✓ Identified' : '✗ Not Identified'} · ${LABELS[need.urgency]}` },
    { label: 'Timeline', score: Math.min(timelineScore, 25), detail: LABELS[timeline] },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-3xl font-bold">{totalScore}</span>
        <span className="text-sm text-muted-foreground">/ 100</span>
        <Progress value={totalScore} className="flex-1" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {dimensions.map(d => (
          <div key={d.label} className="space-y-1 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{d.label}</span>
              <span className="text-sm font-bold">{d.score}/25</span>
            </div>
            <Progress value={(d.score / 25) * 100} className="h-1.5" />
            <p className="text-xs text-muted-foreground">{d.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
